import { Props as MapProps } from 'google-map-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getCenter } from 'stacks/search/helpers';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import SearchMapMarker from './SearchMapMarker';
import GoogleMap from './GoogleMap';
import { SAN_FRANCISCO_COORDINATES } from './const';
import {
  boundingBoxAtom,
  currentPageAtom,
  disableTrackEventsAtom,
  filterApiPayloadAtom,
  filteredBoatsMetaAtom,
  otherQueryParamsAtom,
  perPageAtom,
  placeBoundingBoxAtom,
  searchActionAtom,
  searchMapAsMoveAtom,
  setMapMoveAtom,
  zoomLevelAtom,
} from '../Filter/filterStore';
import { getBoatCardUrl } from '../utils';

export interface SearchMapProps extends MapProps {
  searchMetaData?: any;
  searchResults?: any;
}

const DEBUG_MAP_VIEWPORT = false;

export default function SearchMap(props: SearchMapProps) {
  const { searchMetaData, searchResults } = props;
  const [defaultCenter, setDefaultCenter] = useState(SAN_FRANCISCO_COORDINATES);
  const router = useRouter();
  const moveMap = useSetAtom(setMapMoveAtom);
  const [zoomLevelAtomValue, setZoomAtom] = useAtom(zoomLevelAtom);
  const searchAsMove = useAtomValue(searchMapAsMoveAtom);
  const placeBoundingBox = useAtomValue(placeBoundingBoxAtom);
  const boundingBox = useAtomValue(boundingBoxAtom);
  const otherQueryParams = useAtomValue<any>(otherQueryParamsAtom);
  const searchAction = useAtomValue(searchActionAtom);
  const searchMeta = useAtomValue(filteredBoatsMetaAtom);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef(null);
  const mapInternalRef = useRef(null);

  const currentPage = useAtomValue(currentPageAtom);
  const perPage = useAtomValue(perPageAtom);
  const filterApiPayload = useAtomValue(filterApiPayloadAtom);
  const setDisableTrackEvents = useSetAtom(disableTrackEventsAtom);

  const { lat, lng } = otherQueryParams || {};

  useEffect(() => {
    if (mapLoaded && mapRef.current) {
      const locCenter = getCenter(boundingBox, Number(lat), Number(lng), searchMeta);
      if (locCenter?.lat && !Number.isNaN(locCenter?.lat)) {
        setDefaultCenter(locCenter);
      }
    }
  }, [searchResults, lat, lng, searchMeta, mapLoaded, boundingBox]);

  useEffect(() => {
    if (mapLoaded && mapRef.current) {
      if (
        placeBoundingBox.sw_lat === 0 &&
        placeBoundingBox.ne_lat === 0 &&
        placeBoundingBox.sw_lng === 0 &&
        placeBoundingBox.ne_lng === 0
      ) {
        mapRef.current.setZoom(Number.isNaN(zoomLevelAtomValue) ? 11 : zoomLevelAtomValue);
      } else {
        const latLngBounds = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(placeBoundingBox.sw_lat, placeBoundingBox.sw_lng),
          new window.google.maps.LatLng(placeBoundingBox.ne_lat, placeBoundingBox.ne_lng)
        );
        mapRef.current?.fitBounds(latLngBounds);
      }
    }
  }, [placeBoundingBox, mapLoaded]);

  const onDragEnd = useCallback(
    (isScrolling: boolean, zoomLevel?: number) => {
      if (!mapRef.current) {
        return;
      }

      const bounds = mapRef.current.getBounds();
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      const calculatedBoundingBox = {
        sw_lat: sw.lat(),
        sw_lng: sw.lng(),
        ne_lat: ne.lat(),
        ne_lng: ne.lng(),
      };
      const mapCenter = bounds.getCenter();

      const currentCenter = new window.google.maps.LatLng(defaultCenter?.lat, defaultCenter?.lng);
      const distance = window.google.maps.geometry.spherical.computeDistanceBetween(mapCenter, currentCenter);

      const DRAG_TOLERANCE = 100;

      if ((distance >= DRAG_TOLERANCE || isScrolling) && searchAsMove) {
        if (DEBUG_MAP_VIEWPORT) {
          // eslint-disable-next-line no-new
          new window.google.maps.Rectangle({
            strokeColor: '#FF0000',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#FF0000',
            fillOpacity: 0.35,
            map: mapRef.current,
            bounds: {
              north: ne.lat(),
              south: sw.lat(),
              east: ne.lng(),
              west: sw.lng(),
            },
          });
        }

        moveMap({
          boundingBox: calculatedBoundingBox,
          zoomLevel: zoomLevel || mapRef.current.getZoom(),
        });
      }
      setDisableTrackEvents(true);
    },
    [defaultCenter, searchAsMove, moveMap, searchAction, setDisableTrackEvents]
  );

  useEffect(() => {
    const radiusSearch = searchMetaData?.radius_search;
    if (
      (radiusSearch && searchMetaData?.geography?.original) ||
      (searchMetaData?.geography?.original && router?.query.sem_account_id)
    ) {
      const { viewport, lat: originalLat, lng: originalLng } = searchMetaData?.geography?.original || {};
      mapRef.current?.fitBounds(
        new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(viewport[0], viewport[1]),
          new window.google.maps.LatLng(viewport[2], viewport[3])
        ),
        0
      );

      mapRef.current?.setCenter(new window.google.maps.LatLng(originalLat, originalLng));
      if (router.query.sem_account_id) {
        setZoomAtom(mapRef.current?.getZoom());
      }
    }
    if (!radiusSearch && searchMetaData?.geography?.extendable) {
      const { viewport } = searchMetaData.geography.extendable;
      mapRef.current?.fitBounds(
        new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(viewport[0], viewport[1]),
          new window.google.maps.LatLng(viewport[2], viewport[3])
        ),
        0
      );
    }
  }, [searchMetaData, router.query.sem_account_id, searchAction, setZoomAtom]);

  const [clickedID, setClickedID] = useState<string | null>(null);

  const toggleDetail = (id, locLat, locLng) => () => {
    if (clickedID === id) {
      setClickedID(null);
    } else {
      setClickedID(id);
      setDefaultCenter({ lat: locLat, lng: locLng });
    }
  };

  return (
    <GoogleMap
      debounced
      onDragEndAction={onDragEnd}
      resetBoundsOnResize
      yesIWantToUseGoogleMapApiInternals
      key="search-map"
      defaultZoom={12}
      center={defaultCenter}
      defaultCenter={SAN_FRANCISCO_COORDINATES}
      map={mapRef.current}
      options={() => ({
        gestureHandling: 'greedy',
        disableDefaultUI: true,
      })}
      onGoogleApiLoaded={({ map, maps }) => {
        // Intermittent bug: "google-map-react" v2.2.2 causes a bug where the map boundaries are calc'd incorrectly
        // We're currently pinned to v2.2.1 until the bug is fixed
        mapRef.current = map;
        mapInternalRef.current = maps;
        apiIsLoaded(map, boundingBox, setZoomAtom, searchAction, router.query.zoom_level);
        setMapLoaded(true);
      }}
    >
      {searchResults.map((boat, index) => (
        <SearchMapMarker
          url={getBoatCardUrl({
            filterApiPayload,
            boatId: boat.boat_public_id,
            pos: (currentPage - 1) * perPage + index,
          })}
          showDetail={clickedID === boat?.boat_public_id}
          key={boat?.boat_public_id}
          lat={boat?.location?.lat}
          lng={boat?.location?.lng}
          onClick={toggleDetail(boat?.boat_public_id, boat?.location?.lat, boat?.location?.lng)}
          boat={boat}
          setClickedID={setClickedID}
        />
      ))}
    </GoogleMap>
  );
}

const apiIsLoaded = (map, boundingBox, setZoomAtom, searchAction, isZoomSet) => {
  map.setClickableIcons(false);

  const nonEmptyBB = Object.values(boundingBox).filter((i) => !!i);

  map.fitBounds(
    new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(boundingBox.ne_lat, boundingBox.ne_lng),
      new window.google.maps.LatLng(boundingBox.sw_lat, boundingBox.sw_lng)
    ),
    0
  );
  if (searchAction === 'search' && nonEmptyBB.length !== 0 && !isZoomSet) {
    setZoomAtom(map.getZoom());
  }
};
